import { useEffect, useState } from 'react';
import {
  EXTERNAL_COST_LINKS,
  type ExternalCost,
  type ExternalCostLink,
  type SaveExternalCostRequest,
} from '@forecastify/shared';
import { Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { EditorDialog } from '../settings/editor-dialog';
import { DateField } from '../date-field';
import { Field } from '../card';
import {
  keepCurrent,
  useContractPeriods,
  useProjectContracts,
  useProjectOwnerChoices,
} from './project-owners';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A külső költség szerkesztője — C56.                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE LINK PICKER CHANGES WHAT THE FORM ASKS FOR NEXT, AND THAT IS THE WHOLE SHAPE.** A
 * contract-level cost needs the contract; a line-item one needs the deliverable; an occurrence one
 * needs a recurring contract AND which period (5 Nov 2026: there is no project level any more, and
 * the period option shows only when the project HAS a recurring contract). Rendering all three and disabling two would offer a reader
 * fields that can only produce a refusal — `0132`'s check would reject a cost naming both.
 *
 * ⚠️ **The recurrence is the second such branch.** *Egyszeri* hides the step and the end date, which
 * mean nothing without it.
 */
export interface ExternalCostDraft {
  id: string | null;
  projectId: string;
  name: string;
  incurredOn: string;
  amount: string;
  currencyCode: string;
  linkKind: ExternalCostLink;
  lineItemId: string;
  contractId: string;
  occurrenceStartsOn: string;
  recurring: boolean;
  recurrenceUnit: 'week' | 'month';
  recurrenceStep: string;
  recurrenceUntil: string;
  ownerMemberId: string;
  version: number | undefined;
}

export function draftOf(cost: ExternalCost): ExternalCostDraft {
  return {
    id: cost.id,
    projectId: cost.projectId,
    name: cost.name,
    incurredOn: cost.incurredOn,
    amount: cost.amount ?? '',
    currencyCode: cost.currencyCode ?? '',
    linkKind: cost.linkKind,
    lineItemId: cost.lineItemId ?? '',
    contractId: cost.contractId ?? '',
    occurrenceStartsOn: cost.occurrenceStartsOn ?? '',
    recurring: cost.recurrenceUnit !== null,
    recurrenceUnit: cost.recurrenceUnit ?? 'month',
    recurrenceStep: String(cost.recurrenceStep ?? 1),
    recurrenceUntil: cost.recurrenceUntil ?? '',
    ownerMemberId: cost.ownerMemberId,
    version: cost.version,
  };
}

export function emptyDraft(
  projectId: string,
  currencyCode: string,
  ownerMemberId: string,
): ExternalCostDraft {
  return {
    id: null,
    projectId,
    name: '',
    /* ⚠️ Today, because a cost is almost always entered on the day its invoice arrives. */
    incurredOn: new Date().toISOString().slice(0, 10),
    amount: '',
    currencyCode,
    /* 5 Nov 2026 · the contract is the coarsest home a cost has; its only one is picked below. */
    linkKind: 'contract',
    lineItemId: '',
    contractId: '',
    occurrenceStartsOn: '',
    recurring: false,
    recurrenceUnit: 'month',
    recurrenceStep: '1',
    recurrenceUntil: '',
    ownerMemberId,
    version: undefined,
  };
}

export function ExternalCostDialog(props: {
  readonly draft: ExternalCostDraft;
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
  /** R1 · `closed` ones are not offered — except the one the cost already sits on. */
  readonly lineItems: readonly {
    readonly id: string;
    readonly name: string;
    readonly closed?: boolean;
  }[];
  /**
   * ⚠️ R3 · the WORKSPACE's people — used only to name a current owner who is no longer on the
   * project. The owner picker offers the project's active members (`useProjectOwnerChoices`).
   */
  readonly members: readonly { readonly id: string; readonly name: string }[];
  readonly serverError: string | null;
  readonly onCancel: () => void;
  readonly onSave: (body: SaveExternalCostRequest) => void;
  /**
   * ⚠️ ux-pass-2 · ONE question, opened from its rail row on the detail, where every single-value
   * field is edited in place. `'link'` is what the cost is attached to, `'recurrence'` how often it
   * recurs — each several fields that only make sense together. The rest of the draft rides along
   * unchanged, so the body is still the whole record the `PUT` takes.
   */
  readonly focus?: 'link' | 'recurrence';
}) {
  const t = useT();
  const all = props.focus === undefined;
  const showLink = all || props.focus === 'link';
  const showRecurrence = all || props.focus === 'recurrence';
  const [draft, setDraft] = useState(props.draft);

  const patch = (change: Partial<ExternalCostDraft>): void => {
    setDraft((current) => ({ ...current, ...change }));
  };

  /* 5 Nov 2026 · the project's contracts, read here — every caller wanted the same list. */
  const contracts = useProjectContracts(draft.projectId);
  const recurringContracts = (contracts ?? []).filter((one) => one.recurring);
  /* A project with ONE contract has the answer already: a new contract-level cost takes it. */
  const only = contracts?.length === 1 ? contracts[0] : undefined;
  useEffect(() => {
    if (only === undefined) return;
    setDraft((current) =>
      current.linkKind === 'contract' && current.contractId === ''
        ? { ...current, contractId: only.id }
        : current,
    );
  }, [only]);
  /* The kinds on offer: a period only where a recurring contract exists — or the one it is on. */
  const linkKinds = EXTERNAL_COST_LINKS.filter(
    (kind) =>
      kind !== 'occurrence' || recurringContracts.length > 0 || props.draft.linkKind === kind,
  );
  /* 5 Nov 2026 · the PICKED contract's periods, read on the pick. */
  const periods = useContractPeriods(draft.linkKind === 'occurrence' ? draft.contractId : '');
  /* ⚠️ No contract at all: no cost, until one is added (the product owner, 5 Nov 2026). */
  const noContract = contracts !== null && contracts.length === 0;

  /* R3 · the project's active members, plus the owner an existing cost already has. A NEW cost
     keeps nobody it was defaulted to — that default came from a workspace list. */
  const projectOwners = useProjectOwnerChoices(draft.projectId);
  const ownerOptions = keepCurrent(
    projectOwners ?? [],
    props.draft.id === null ? '' : props.draft.ownerMemberId,
    props.members,
  );
  const ownerKept = draft.id !== null && draft.ownerMemberId === props.draft.ownerMemberId;
  const ownerOffered = ownerKept || ownerOptions.some((one) => one.id === draft.ownerMemberId);
  /* R1 · a closed line item is not offered, unless the cost already sits on it. */
  const lineItemOptions = props.lineItems.filter(
    (one) => one.closed !== true || one.id === props.draft.lineItemId,
  );

  const amount = Number(draft.amount.replace(/\s/g, '').replace(',', '.'));
  const valid =
    draft.name.trim() !== '' &&
    Number.isFinite(amount) &&
    amount >= 0 &&
    draft.ownerMemberId !== '' &&
    /* R3 · nobody the picker would not offer — the defaulted owner of a new cost included. */
    (projectOwners === null || ownerOffered) &&
    /* R1 · nor a closed line item newly picked (the one it already sits on stays). */
    (draft.linkKind !== 'line_item' ||
      draft.lineItemId === props.draft.lineItemId ||
      lineItemOptions.some((one) => one.id === draft.lineItemId)) &&
    (draft.linkKind !== 'line_item' || draft.lineItemId !== '') &&
    (draft.linkKind !== 'contract' || draft.contractId !== '') &&
    (draft.linkKind !== 'occurrence' ||
      (draft.contractId !== '' && draft.occurrenceStartsOn !== '')) &&
    !noContract;

  return (
    <EditorDialog
      open
      title={
        props.focus === 'link'
          ? t('extcost.field.link')
          : props.focus === 'recurrence'
            ? t('extcost.field.recurrence')
            : draft.id === null
              ? t('extcost.dialog.new')
              : t('extcost.dialog.edit')
      }
      {...(all ? { description: t('extcost.dialog.hint') } : {})}
      saveLabel={draft.id === null ? t('common.create') : t('common.save')}
      saveDisabled={!valid}
      onClose={props.onCancel}
      onSave={() => {
        const recurrence = draft.recurring
          ? {
              recurrenceUnit: draft.recurrenceUnit,
              recurrenceStep: Math.max(1, Number(draft.recurrenceStep) || 1),
              recurrenceUntil: draft.recurrenceUntil === '' ? null : draft.recurrenceUntil,
            }
          : { recurrenceUnit: null, recurrenceStep: null, recurrenceUntil: null };

        /* ⚠️ The link is spread as ONE of three shapes — the discriminated union refuses a body
           that names two, which is the same rule `0132` enforces in the table. */
        const link: SaveExternalCostRequest =
          draft.linkKind === 'line_item'
            ? { linkKind: 'line_item', lineItemId: draft.lineItemId, ...common() }
            : draft.linkKind === 'occurrence'
              ? {
                  linkKind: 'occurrence',
                  contractId: draft.contractId,
                  occurrenceStartsOn: draft.occurrenceStartsOn,
                  ...common(),
                }
              : { linkKind: 'contract', contractId: draft.contractId, ...common() };

        function common() {
          return {
            projectId: draft.projectId,
            name: draft.name.trim(),
            incurredOn: draft.incurredOn,
            amount,
            currencyCode: draft.currencyCode,
            ownerMemberId: draft.ownerMemberId,
            ...recurrence,
            ...(draft.version === undefined ? {} : { version: draft.version }),
          };
        }

        props.onSave(link);
      }}
    >
      {props.serverError === null ? null : (
        <p className="text-destructive text-[12px]">{props.serverError}</p>
      )}
      {noContract ? (
        <p className="text-muted-foreground text-[12px]">{t('extcost.noContract')}</p>
      ) : null}

      {!all ? null : (
        <>
          <Field label={t('extcost.field.name')}>
            <TextInput
              value={draft.name}
              autoFocus
              placeholder={t('extcost.field.namePlaceholder')}
              onChange={(event) => {
                patch({ name: event.target.value });
              }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('extcost.field.amount')}>
              <TextInput
                value={draft.amount}
                className="text-right tabular-nums"
                onChange={(event) => {
                  patch({ amount: event.target.value });
                }}
              />
            </Field>
            <Field label={t('extcost.field.currency')}>
              <Select
                value={draft.currencyCode}
                onChange={(event) => {
                  const value = event.target.value;
                  patch({ currencyCode: value });
                }}
                options={props.currencies.map((one) => ({
                  value: one.code,
                  label: `${one.code} · ${one.symbol}`,
                }))}
              />
            </Field>
          </div>

          <Field label={t('extcost.field.date')}>
            <DateField
              label={t('extcost.field.date')}
              value={draft.incurredOn}
              onChange={(value) => {
                patch({ incurredOn: value });
              }}
            />
          </Field>
        </>
      )}

      {!showLink ? null : (
        <>
          <Field label={t('extcost.field.link')}>
            <Select
              value={draft.linkKind}
              onChange={(event) => {
                const value = event.target.value;
                /* ⚠️ The other two arms are CLEARED on a change: a cost that kept a stale line item
               while pointing at an occurrence is what the table's check refuses, and the form
               should not be able to build one. */
                patch({
                  linkKind: value as ExternalCostLink,
                  lineItemId: '',
                  contractId: '',
                  occurrenceStartsOn: '',
                });
              }}
              options={linkKinds.map((kind) => ({
                value: kind,
                label: t(
                  kind === 'line_item'
                    ? 'extcost.link.lineItem'
                    : kind === 'occurrence'
                      ? 'extcost.link.occurrence'
                      : 'extcost.link.contract',
                ),
              }))}
            />
          </Field>

          {draft.linkKind !== 'contract' ? null : (
            <Field label={t('extcost.field.contract')}>
              <Select
                searchable
                searchPlaceholder={t('list.searchOptions')}
                value={draft.contractId}
                onChange={(event) => {
                  patch({ contractId: event.target.value });
                }}
                options={[
                  { value: '', label: t('extcost.field.pickContract') },
                  ...(contracts ?? []).map((one) => ({
                    value: one.id,
                    label: one.code,
                    hint: one.hint,
                  })),
                ]}
              />
            </Field>
          )}

          {draft.linkKind !== 'line_item' ? null : (
            <Field label={t('extcost.link.lineItem')}>
              <Select
                /* U-14 · a list of records, so it searches. */
                searchable
                searchPlaceholder={t('list.searchOptions')}
                value={draft.lineItemId}
                onChange={(event) => {
                  const value = event.target.value;
                  patch({ lineItemId: value });
                }}
                /* ⚠️ A blank first option IS the placeholder on a native select — the control has
               no `placeholder` prop, and an unpicked one would otherwise show the first item as
               though it had been chosen. */
                options={[
                  { value: '', label: t('extcost.field.pickLineItem') },
                  ...lineItemOptions.map((one) => ({ value: one.id, label: one.name })),
                ]}
              />
            </Field>
          )}

          {draft.linkKind !== 'occurrence' ? null : (
            <>
              <Field label={t('extcost.link.occurrence')}>
                <Select
                  /* U-14 · a list of records, so it searches. */
                  searchable
                  searchPlaceholder={t('list.searchOptions')}
                  value={draft.contractId}
                  onChange={(event) => {
                    const value = event.target.value;
                    patch({ contractId: value, occurrenceStartsOn: '' });
                  }}
                  /* 5 Nov 2026 · only a contract with periods — the others have none to pick. */
                  options={[
                    { value: '', label: t('extcost.field.pickContract') },
                    ...recurringContracts.map((one) => ({
                      value: one.id,
                      label: one.code,
                      hint: one.hint,
                    })),
                  ]}
                />
              </Field>
              <Field label={t('extcost.field.period')}>
                <Select
                  value={draft.occurrenceStartsOn}
                  onChange={(event) => {
                    const value = event.target.value;
                    patch({ occurrenceStartsOn: value });
                  }}
                  options={[
                    { value: '', label: t('extcost.field.pickPeriod') },
                    ...periods.map((one) => ({ value: one.startsOn, label: one.label })),
                  ]}
                />
              </Field>
            </>
          )}
        </>
      )}

      {!showRecurrence ? null : (
        <>
          <Field label={t('extcost.field.recurrence')}>
            <Select
              value={draft.recurring ? 'recurring' : 'once'}
              onChange={(event) => {
                const value = event.target.value;
                patch({ recurring: value === 'recurring' });
              }}
              options={[
                { value: 'once', label: t('extcost.recur.once') },
                { value: 'recurring', label: t('extcost.recur.repeats') },
              ]}
            />
          </Field>

          {!draft.recurring ? null : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('extcost.field.every')}>
                  <TextInput
                    value={draft.recurrenceStep}
                    className="text-right tabular-nums"
                    onChange={(event) => {
                      patch({ recurrenceStep: event.target.value });
                    }}
                  />
                </Field>
                <Field label={t('extcost.field.unit')}>
                  <Select
                    value={draft.recurrenceUnit}
                    onChange={(event) => {
                      const value = event.target.value;
                      patch({ recurrenceUnit: value === 'week' ? 'week' : 'month' });
                    }}
                    options={[
                      { value: 'month', label: t('extcost.unit.month') },
                      { value: 'week', label: t('extcost.unit.week') },
                    ]}
                  />
                </Field>
              </div>
              <Field label={t('extcost.field.until')}>
                <DateField
                  label={t('extcost.field.until')}
                  value={draft.recurrenceUntil}
                  onChange={(value) => {
                    patch({ recurrenceUntil: value });
                  }}
                />
              </Field>
              {/**
               * ⚠️⚠️ **THE SENTENCE THE DETAIL SCREEN SAYS, SAID BEFORE THE FIGURE IS TYPED.** A
               * recurring cost charges its FULL amount to every period it touches, which is the opposite
               * of how a line item's budget behaves — somebody who has been editing those all morning
               * will read this field as *spread across*, and be out by a factor of twelve.
               */}
              <p className="text-muted-foreground text-[11.5px]">{t('extcost.recur.note')}</p>
            </>
          )}
        </>
      )}

      {!all ? null : (
        <Field label={t('extcost.field.owner')}>
          <Select
            /* U-14 · a list of records, so it searches. */
            searchable
            searchPlaceholder={t('list.searchOptions')}
            value={ownerOffered ? draft.ownerMemberId : ''}
            onChange={(event) => {
              const value = event.target.value;
              patch({ ownerMemberId: value });
            }}
            /* R3 · the project's active members; a blank first option, so a default that is not
               one of them reads as unpicked rather than as somebody. */
            options={[
              { value: '', label: t('common.pleaseChoose') },
              ...ownerOptions.map((one) => ({ value: one.id, label: one.name })),
            ]}
          />
        </Field>
      )}
    </EditorDialog>
  );
}

/**
 * An existing cost as a save body, with one field replaced.
 *
 * ⚠️⚠️ **A PATCH ENDPOINT WOULD HAVE BEEN THE FOURTH WAY TO WRITE THIS ROW.** `PUT` takes the whole
 * record and `0132`'s check constraint reads all three link columns at once, so a partial write
 * would need the same arm logic on the server a second time. Rebuilding the body from what the
 * screen already holds keeps one writer — and the `version` travels, so a stale metafield save is
 * refused like any other stale write.
 */
/**
 * What the cost detail edits in place — ux-pass-2, 14 Oct: *"This should be inline like all other
 * parts … no edit button needed."* The link and the recurrence are not here: each is several
 * fields at once, and moves through `ExternalCostDialog`'s `focus`.
 */
export interface CostPatch {
  readonly name?: string;
  readonly incurredOn?: string;
  readonly amount?: number;
  readonly currencyCode?: string;
  readonly ownerMemberId?: string;
  readonly metafields?: Record<string, unknown>;
}

export function bodyOfCost(cost: ExternalCost, patch: CostPatch): SaveExternalCostRequest {
  const link =
    cost.linkKind === 'line_item'
      ? { linkKind: 'line_item' as const, lineItemId: cost.lineItemId ?? '' }
      : cost.linkKind === 'occurrence'
        ? {
            linkKind: 'occurrence' as const,
            contractId: cost.contractId ?? '',
            occurrenceStartsOn: cost.occurrenceStartsOn ?? '',
          }
        : { linkKind: 'contract' as const, contractId: cost.contractId ?? '' };

  return {
    projectId: cost.projectId,
    name: patch.name ?? cost.name,
    incurredOn: patch.incurredOn ?? cost.incurredOn,
    /* 22 Oct 2026 · a cost whose figures were withheld has none to carry over — the server's own
       validation refuses the NaN and the empty code rather than this writing a zero. */
    amount: patch.amount ?? Number(cost.amount ?? Number.NaN),
    currencyCode: patch.currencyCode ?? cost.currencyCode ?? '',
    recurrenceUnit: cost.recurrenceUnit,
    recurrenceStep: cost.recurrenceStep,
    recurrenceUntil: cost.recurrenceUntil,
    ownerMemberId: patch.ownerMemberId ?? cost.ownerMemberId,
    metafields: patch.metafields ?? cost.metafields,
    version: cost.version,
    ...link,
  };
}
